import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { BookOpenIcon, CheckIcon } from '@heroicons/react/24/outline';

import MainButton from '@/components/ui/custom/buttons/MainButton';
import Badge from '@/components/ui/custom/dataDisplay/Badge';
import { ModalHomework } from '@/components/ui/custom/modals/ModalHomework/ModalHomework';
import { HomeworkActivityProgress } from '@/models/HomeworkActivity';
import { handleTeacherHomeworkRevision } from '@/utils/teacherHomeworkNotification';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import Skeleton from '@/components/ui/custom/Skeleton';
import Text from '@/components/ui/custom/dataDisplay/Text';
import { getErrorMessage } from '@/utils/getErrorMessage';
import {
  homeworkActivitiesQueryKeys,
  homeworkProgressQueryKeys,
  scheduledLessonsQueryKeys,
} from '@/data/services/querykeys';
import { useActiveStudents } from '../scheduled-lesson-report/StudentReports.hook';
import useListService from '@/data/hook/useListService';
import { formatLessonName } from '@/utils/lesson/name';

type HomeworkGradeMarkerProps = {
  klassId: number;
  scheduledLessonOrder?: number;
};

export const HomeworkGradeMarker = ({
  klassId,
  scheduledLessonOrder,
}: HomeworkGradeMarkerProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'manageScheduledLessonReport.homeworkGradeMarker',
  });

  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);

  const {
    data: { results: previousScheduledLessons } = {},
    isInitialLoading: loadingPrevScheduledLesson,
    error: previousScheduledLessonError,
  } = useQuery({
    enabled: !!scheduledLessonOrder,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    ...scheduledLessonsQueryKeys.list({
      klassId,
      isActive: true,
      orderLt: scheduledLessonOrder,
      ordering: '-datetime',
      pageSize: 1,
    }),
  });

  const previousScheduledLesson = previousScheduledLessons?.at(0);

  const { enrollments } = useActiveStudents({
    active: true,
    klassId,
    scheduledLesson: previousScheduledLesson,
  });

  const scheduledLessonErrorDetail = getErrorMessage(
    previousScheduledLessonError,
  );

  const {
    data: { results: previousHomeworks } = {},
    isInitialLoading: loadingHomework,
  } = useQuery({
    ...homeworkActivitiesQueryKeys.list({
      activity: previousScheduledLesson?.homeworkActivity ?? 0,
    }),
    enabled: !!previousScheduledLesson?.homeworkActivity,
  });
  const studentIds = useMemo(
    () => enrollments.map(({ student }) => student.id),
    [enrollments],
  );
  const previousHomework = previousHomeworks?.at(0);

  const homeworkId = previousHomework?.id ?? 0;
  const { results: homeworkProgresses } = useListService({
    staleTime: REQUEST_STALE_TIME_IN_MS,
    enabled: !!homeworkId && studentIds.length > 0,
    ...homeworkProgressQueryKeys.list(homeworkId ?? 0, {
      studentId: studentIds,
    }),
  });

  const filterPendingHomeworks = (
    homeworkProgress?: HomeworkActivityProgress,
  ) => {
    if (homeworkProgress) {
      const {
        status,
        homework: { assignmentType },
      } = homeworkProgress;

      return (
        handleTeacherHomeworkRevision(status, assignmentType) === 'pending'
      );
    }
    return false;
  };

  const homeworkGradeNotification = homeworkProgresses.filter(
    filterPendingHomeworks,
  ).length;

  const haveNotification = homeworkGradeNotification !== 0;

  const loading = loadingPrevScheduledLesson || loadingHomework;

  if (loading)
    return <Skeleton className="h-8 rounded-md bg-primary/40 w-56" />;

  if (scheduledLessonErrorDetail)
    return (
      <Text className="text-primary/40" text={scheduledLessonErrorDetail} />
    );

  if (previousScheduledLesson?.homeworkActivity)
    return (
      <div>
        <ModalHomework
          homeworkProgresses={homeworkProgresses}
          isVisible={isOpenModal}
          onClickCancel={() => setIsOpenModal(false)}
          lessonName={formatLessonName(
            previousScheduledLesson.lesson,
            previousScheduledLesson,
          )}
          enrollments={enrollments}
        />

        <Badge count={homeworkGradeNotification} position="-left-1 bottom-6">
          <MainButton
            color="custom"
            size="medium"
            dataTestId="homeworkGradeMarkerButton"
            text={t('homeworkPreviousButton')}
            icon={haveNotification ? <BookOpenIcon /> : <CheckIcon />}
            className={`select-none py-2 px-2.5 text-base-100 ${
              haveNotification
                ? 'bg-primary'
                : 'bg-success cursor-default disabled'
            }`}
            onClick={() => haveNotification && setIsOpenModal(true)}
          />
        </Badge>
      </div>
    );

  return <Fragment />;
};
